import { useState, type FC } from 'react';
import classNames from 'classnames';
import { useDebouncedCallback } from '@mantine/hooks';
import { Group, Input, Select } from '@mantine/core';
import { useUnit } from 'effector-react';
import { PhoneInput, validatePhoneNumber } from '~/shared/ui/PhoneInput';
import { CardInput, validateCardNumber } from '~/shared/ui/CardInput';
import { $filters, setFilters, type Filters } from '../../model';
import { requisites } from './constants';
import styles from './RequisiteFilter.module.css';

type RequisiteType = (typeof requisites)[number]['value'];

const debounceMs = 300;

const getInitialRequisiteType = (filters: Filters): RequisiteType => {
    return filters.requisiteType;
};

const getRequisiteValue = (filters: Filters) => {
    return filters.requisiteSuffixes?.[0] ?? '';
};

const getRequisiteError = (type: RequisiteType, value: string) => {
    return type === 'sbp' ? validatePhoneNumber(value) : validateCardNumber(value);
};

const createRequisiteFilter = (type: RequisiteType, value: string): Filters['requisiteSuffixes'] | undefined => {
    const trimmed = value.trim();

    if (!trimmed || getRequisiteError(type, trimmed)) {
        return undefined;
    }

    return [trimmed];
};

export const RequisiteFilter: FC = () => {
    const { filters } = useUnit({
        filters: $filters,
    });

    const initialType = getInitialRequisiteType(filters);
    const initialValue = getRequisiteValue(filters);

    const [type, setType] = useState<RequisiteType>(initialType);
    const [value, setValue] = useState(initialValue);
    const [error, setError] = useState<string | undefined>(() => {
        return getRequisiteError(initialType, initialValue);
    });

    const debouncedSetFilter = useDebouncedCallback((value: string, currentType: RequisiteType) => {
        setFilters({
            requisiteType: currentType,
            requisiteSuffixes: createRequisiteFilter(currentType, value),
        });
    }, debounceMs);

    const handleInputChange = (value: string) => {
        setValue(value);
        setError(getRequisiteError(type, value));
        debouncedSetFilter(value, type);
    };

    const handleClearInput = () => {
        debouncedSetFilter.cancel();
        setFilters({
            requisiteType: type,
            requisiteSuffixes: undefined,
        });
        setValue('');
        setError(undefined);
    };

    const handleTypeChange = (value: string | null) => {
        if (!value) {
            return;
        }

        debouncedSetFilter.cancel();
        setFilters({
            requisiteType: value as RequisiteType,
            requisiteSuffixes: undefined,
        });
        setValue('');
        setError(undefined);
        setType(value as RequisiteType);
    };

    const inputProps = {
        size: 'xs' as const,
        className: styles.inputRoot,
        onChange: handleInputChange,
        value: value,
        rightSection: value ? <Input.ClearButton onClick={handleClearInput} /> : undefined,
        rightSectionPointerEvents: 'auto' as const,
        classNames: {
            input: styles.valueInput,
            wrapper: styles.innerWrapper,
        },
    };

    return (
        <Input.Wrapper
            size='xs'
            label='Реквизиты'
            labelProps={{
                fz: 'xs',
            }}
            error={error}
        >
            <Group
                className={classNames(styles.control, error && styles.controlWithError)}
                align='stretch'
                gap={0}
                wrap='nowrap'
            >
                <Select
                    size='xs'
                    data={requisites}
                    onChange={handleTypeChange}
                    value={type}
                    w={120}
                    className={styles.selectRoot}
                    classNames={{
                        input: styles.selectInput,
                        section: styles.section,
                        wrapper: styles.innerWrapper,
                    }}
                />

                {type === 'sbp' ? (
                    <PhoneInput {...inputProps} />
                ) : (
                    <CardInput
                        {...inputProps}
                        autoComplete='none'
                    />
                )}
            </Group>
        </Input.Wrapper>
    );
};
